// TODO: make type safe
import React, { useEffect, useRef, useState } from 'react';
import { RocheUIStoryTeaser, RocheUIStoryTeaserLayout, RocheUIStoryType } from '@repo/roche-ui';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { StoryItem, StoryTeaserService } from '../../services/story-teaser';
import { StoryblokService } from '../../services/storyblok';
import { parseAssetSource } from '../../utils/asset';
import { StringService } from '../../services/string';
import { useTranslation } from '../../hooks/use-translation';
import {
  DefaultLinkClickData,
  OnSiteSearchClickLink,
  OnSiteSearchEvent,
  TrackingEvent,
} from '../../tracking-services/types';
import { AdobeTracking } from '../../tracking-services';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';

const renderWarning = (keyForChildren: string | number, wrapperTag: string, slot: string) => {
  const Wrapper = wrapperTag as React.ElementType;
  const description = `
    If you wish to proceed with this action, please publish the linked page first.
    Note that if the linked page on the teaser remains unpublished,
    this teaser will not show up on the live version of the page.
  `;
  return (
    // ConditionalWrapper is needed to work within <roche-slider-item>
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
    >
      <RocheUIStoryTeaser
        slot={slot}
        layout={'story-teaser--variant-a' as RocheUIStoryTeaserLayout}
        teaserTitle='WARNING: This teaser is not published!'
        teaserDescription={description}
        unpublished={true}
        teaserType={RocheUIStoryType.DEFAULT}
        teaserImage={''}
        teaserUrl={''}
        teaserTagLabel={''}
      />
    </ConditionalWrapper>
  );
};

export const RocheStoryTeaser = ({ blok, updateHoistedState, slot, wrapperTag, keyForChildren }: any): JSX.Element => {
  const Wrapper = wrapperTag as React.ElementType;
  const [teaser, setTeaser] = useState<StoryItem>(blok.story?.story?.preRenderContent || ({} as StoryItem));
  const [hasUpdatedHoistedState, setHasUpdatedHoistedState] = useState(false);

  const storyTeaserRef = useRef<HTMLDivElement>(null);
  const translations = {
    readStory: useTranslation('read the story'),
    [RocheUIStoryType.PODCAST]: useTranslation('podcast'),
    [RocheUIStoryType.SERIES]: useTranslation('series'),
    [RocheUIStoryType.SLIDESHOW]: useTranslation('slideshow'),
    [RocheUIStoryType.VIDEO]: useTranslation('video'),
  };
  const storyType =
    teaser.content?.teaser_type && teaser.content?.teaser_type !== ''
      ? (teaser.content?.teaser_type as RocheUIStoryType)
      : undefined;
  const translatedTag =
    teaser.content?.teaser_type &&
    teaser.content?.teaser_type !== '' &&
    teaser.content?.teaser_type !== RocheUIStoryType.DEFAULT
      ? StringService.capitalize(translations[teaser.content?.teaser_type as RocheUIStoryType])
      : undefined;

  useEffect(() => {
    const storyId = blok.story?.story?.id;
    const hasTeaser = teaser && Object.keys(teaser).length > 0;
    const { _uid: blokUid } = blok;
    if (hasUpdatedHoistedState) {
      // if we have already informed the parent component we return
      return;
    }

    if (storyId && hasTeaser) {
      // if we have the teaser already, we inform the parent component and return
      updateHoistedState?.(1, 1, blokUid, teaser);
      setHasUpdatedHoistedState(true);
      return;
    }

    if (storyId && !hasTeaser) {
      // if we do not have the teaser we fetch it and inform the parent component
      StoryTeaserService.getStoryTeaser(storyId).then((result) => {
        if (result) {
          setTeaser(result);
          updateHoistedState?.(1, 1, blokUid, result);
          setHasUpdatedHoistedState(true);
          return;
        }
        // if we do not get a result we inform the parent component
        // (e.g. teaser-slider) so that it can initialize with a warning element
        updateHoistedState?.(1, 1, '', {});
        setHasUpdatedHoistedState(true);
      });
    }

    // even in live-mode we get the unpublished teaser, we need to inform the parent component
    // so that the parent (e.g. teaser-slider) has the correct number of teasers that are ready.
    // A better solution would be if we only got the teasers that are published in live-mode.
    if (!storyId && !StoryblokService.isInEditor()) {
      updateHoistedState?.(1, 1, '', {});
      setHasUpdatedHoistedState(true);
    }
  }, [teaser, blok, hasUpdatedHoistedState]);

  const teaserImage = teaser.content?.teaser_image?.[0];

  const hasTeaser = teaser && Object.keys(teaser).length > 0;
  if (!hasTeaser && StoryblokService.isInEditor()) {
    return renderWarning(keyForChildren, wrapperTag, slot);
  }

  if (!hasTeaser) {
    return <div></div>;
  }

  return (
    // ConditionalWrapper is needed to work within <roche-slider-item>
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
    >
      <RocheUIStoryTeaser
        ref={storyTeaserRef}
        slot={slot}
        layout={blok.layout}
        scaleOnScroll={blok?.scale_on_scroll}
        teaserType={storyType}
        teaserImage={teaserImage?.brightcove_id || parseAssetSource(teaserImage?.src.filename)}
        teaserTitle={teaser.content?.teaser_title}
        teaserDescription={teaser.content?.teaser_description}
        teaserDate={teaser?.first_published_at}
        teaserUrl={StringService.sanitizeUrl(`/${teaser?.full_slug}`)}
        teaserTagLabel={translatedTag}
        ctaLabel={StringService.capitalize(translations.readStory)}
        onTeaserLinkClick={() => trackStoryTeaserClick(teaser, storyTeaserRef)}
      />
    </ConditionalWrapper>
  );
};

export const trackStoryTeaserClick = (teaser: StoryItem, storyTeaserRef: React.RefObject<HTMLDivElement>) => {
  if (!teaser || !storyTeaserRef.current) return;

  const adobeTracking: DefaultLinkClickData = {
    url: StringService.sanitizeUrl(`/${teaser?.full_slug}`),
    innerText: teaser.content?.teaser_title,
    name: teaser.content?.teaser_title,
    type: 'Story Teaser',
    position: 'rocheStoryTeaser',
    socialMedia: '',
    socialInteraction: '',
  };

  const defaultTracking = {
    event: 'teaser_click',
    component_name: 'roche-teaser',
  };

  const getLatestOnsiteSearchEvent = (): TrackingEvent =>
    window?.oneDataLayer?.filter((event: { event: string }) => event.event === 'onsiteSearch').pop();

  const getElementsInView = () =>
    Array.from(storyTeaserRef.current?.parentElement?.querySelectorAll<HTMLElement>('roche-story-teaser') || []);

  const toggleActiveState = (isActive: boolean): void => {
    if (isActive) {
      storyTeaserRef.current.classList.add('active');
    } else {
      storyTeaserRef.current.classList.remove('active');
    }
  };

  const getActiveElementIndex = (elementsInView: HTMLElement[]): number =>
    elementsInView.findIndex((element) => (element as HTMLElement).classList.contains('active'));

  const getResultsPerPage = (elementsInView: HTMLElement[]): number => {
    const container =
      storyTeaserRef.current.closest('.roche-search') || storyTeaserRef.current.closest('roche-search-list-container');

    return container
      ? parseInt((container as Element)?.getAttribute('data-rpp') ?? elementsInView.length.toString(), 10)
      : elementsInView.length || 0;
  };

  const calculatePaginationData = (
    elementsInView: HTMLElement[],
  ): {
    paginationResultsPerPage: number;
    activeElementIndex: number;
  } => {
    toggleActiveState(true);

    const activeElementIndex = getActiveElementIndex(elementsInView as unknown as HTMLElement[]);
    const paginationResultsPerPage = getResultsPerPage(elementsInView as unknown as HTMLElement[]);

    toggleActiveState(false);

    return { paginationResultsPerPage, activeElementIndex };
  };

  const handleOnsiteSearchClick = (): void => {
    const latestSearchEvent = getLatestOnsiteSearchEvent();
    const elementsInView = getElementsInView();

    const { paginationResultsPerPage, activeElementIndex } = calculatePaginationData(elementsInView);

    const { term, results, filter, searchComponent } =
      (latestSearchEvent as OnSiteSearchEvent)?.content?.onsiteSearch || {};

    const url = new URL(StringService.sanitizeUrl(`/${teaser?.full_slug}`));

    const resultsPosition = (filter?.pagination - 1) * paginationResultsPerPage + activeElementIndex + 1;

    AdobeTracking(
      {
        ...adobeTracking,
        domain: url.host,
        path: url.pathname,
        resultsPosition,
        term,
        results,
        filter,
        searchComponent,
      } as OnSiteSearchClickLink,
      'onsiteSearchClick',
    );
  };

  TrackingService.push({
    ...defaultTracking,
    additionalParameters: {
      teaser_title: teaser.content?.teaser_title,
      teaser_url: StringService.sanitizeUrl(`/${teaser?.full_slug}`),
    },
  });

  if (storyTeaserRef.current.closest('.roche-search') || storyTeaserRef.current.closest('roche-search')) {
    handleOnsiteSearchClick();
  } else {
    AdobeTracking(adobeTracking, 'defaultLinkClick');
  }
};
